import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { paisa } from "./money.js";
/**
 * Online-store coupons (B-86, SF-18; moved from Phase 5 by owner decision 2026-10-30): a code (or automatic), percent
 * with a cap / fixed amount / free delivery, minimum order, dates, limits, product or category scope. POS coupons
 * stay in Phase 5.
 */
const tag = { tag: "coupons" };
export const COUPON_KINDS = ["percent", "amount", "free_delivery"];
export const COUPON_STATUSES = ["active", "paused"];
export const couponCodeSchema = z
    .string()
    .trim()
    .min(3)
    .max(30)
    .regex(/^[A-Za-z0-9_-]+$/)
    .transform((v) => v.toUpperCase());
export const couponSchema = z.object({
    id: z.uuid(),
    code: z.string(),
    kind: z.enum(COUPON_KINDS),
    /** `percent`: basis points (10% = 1000). */
    percentBp: z.int().nullable(),
    /** `amount`: paisa off. */
    amountPaisa: z.int().nullable(),
    /** `percent`: the most it takes off (null: no cap). */
    maxDiscountPaisa: z.int().nullable(),
    minOrderPaisa: z.int(),
    startsAt: z.iso.datetime().nullable(),
    endsAt: z.iso.datetime().nullable(),
    usageLimitTotal: z.int().nullable(),
    usageLimitPerPhone: z.int().nullable(),
    /** Empty: every product. */
    productIds: z.array(z.uuid()),
    /** Products in these categories or below them. */
    categoryIds: z.array(z.uuid()),
    /** Applied without a code when the order qualifies (the best one wins). */
    autoApply: z.boolean(),
    status: z.enum(COUPON_STATUSES),
    usedCount: z.int(),
    version: z.int(),
});
const couponFields = {
    kind: z.enum(COUPON_KINDS),
    percentBp: z.int().min(1).max(10_000).nullable().default(null),
    amountPaisa: paisa().min(1).nullable().default(null),
    maxDiscountPaisa: paisa().min(1).nullable().default(null),
    minOrderPaisa: paisa().min(0).default(0),
    startsAt: z.iso.datetime().nullable().default(null),
    endsAt: z.iso.datetime().nullable().default(null),
    usageLimitTotal: z.int().min(1).nullable().default(null),
    usageLimitPerPhone: z.int().min(1).nullable().default(null),
    productIds: z.array(z.uuid()).max(200).default([]),
    categoryIds: z.array(z.uuid()).max(50).default([]),
    autoApply: z.boolean().default(false),
    status: z.enum(COUPON_STATUSES).default("active"),
};
const valueRule = (c) => c.kind === "percent" ? c.percentBp !== null : c.kind === "amount" ? c.amountPaisa !== null : true;
export const createCouponSchema = z
    .object({ code: couponCodeSchema, ...couponFields })
    .strict()
    .refine(valueRule, {
    message: "Give the percent for a percent coupon, the amount for an amount coupon",
    path: ["kind"],
});
export const updateCouponSchema = z
    .object({ ...couponFields, version: z.int().min(1) })
    .strict()
    .refine(valueRule, {
    message: "Give the percent for a percent coupon, the amount for an amount coupon",
    path: ["kind"],
});
export const listCoupons = defineEndpoint({
    ...tag,
    name: "listCoupons",
    method: "GET",
    path: "/v1/coupons",
    access: { permission: "coupons.view" },
    summary: "The online store's coupons with how often each was used",
    status: 200,
    response: z.object({ data: z.array(couponSchema) }),
});
export const createCoupon = defineEndpoint({
    ...tag,
    name: "createCoupon",
    method: "POST",
    path: "/v1/coupons",
    access: { permission: "coupons.manage" },
    summary: "Create a coupon",
    body: createCouponSchema,
    status: 201,
    response: couponSchema,
    errors: ["CONFLICT"],
});
export const updateCoupon = defineEndpoint({
    ...tag,
    name: "updateCoupon",
    method: "PUT",
    path: "/v1/coupons/{id}",
    access: { permission: "coupons.manage" },
    summary: "Change a coupon (the code stays)",
    params: idParamsSchema,
    body: updateCouponSchema,
    status: 200,
    response: couponSchema,
    errors: ["CONFLICT"],
});
/** Why a code does not apply to this order, for the shopper. */
export const COUPON_PROBLEMS = [
    "COUPON_NOT_APPLICABLE",
    "COUPON_EXPIRED",
    "COUPON_MIN_ORDER",
    "COUPON_LIMIT_REACHED",
];
//# sourceMappingURL=coupons.js.map